import { CheckboxGroupOption } from "@/components/CheckboxGroup";
import { useModalStore } from "@/store/modalStore";
import { useMonthlyCheckStore } from "@/store/monthlyCheckStore";
import { createQueryString } from "@/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { MonthlyInspectionDetailSchema } from "../schemas";
import {
  KEYS_SEARCH_PARAMS,
  MONTHLY_INSPECTION_DETAIL_MODAL_NAME,
} from "./useMonthlyInspectionList";

const useMonthlyInspectionDetail = () => {
  const { id } = useParams();
  const { modals, closeModal } = useModalStore();
  const open = modals[MONTHLY_INSPECTION_DETAIL_MODAL_NAME] || !!id;

  const [searchParams] = useSearchParams();
  const params = Object.fromEntries([...searchParams]);

  const currentPage = Number(params[KEYS_SEARCH_PARAMS.PAGE]) || 1;
  const currentSearch = params[KEYS_SEARCH_PARAMS.SEARCH] || "";

  const {
    monthlyCheck,
    fetchMonthlyCheckById,
    fetchMonthlyChecks,
    monthlyChecks,
    answerMonthlyCheck,
  } = useMonthlyCheckStore();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentCheckboxs, setCurrentCheckboxs] = useState<number[]>([]);

  const inspectionResultOptions: CheckboxGroupOption[] = useMemo(
    () => {
      if (!monthlyCheck?.templates || !Array.isArray(monthlyCheck.templates)) {
        return [];
      }
      
      return monthlyCheck.templates.map((template) => {
        const isHiddenComment = !currentCheckboxs.includes(template.id);

        return {
          label: (
            <div className="min-w-0 space-y-2">
              <p className="text-sm font-semibold leading-5.5 break-all">
                点検項目{template.order}
              </p>
              <p className="text-xs leading-4.5 break-all">
                {template.question}
              </p>
              <div className="min-h-[18px]">
                {!isHiddenComment && (
                  <p className="text-sm leading-4.5 break-all">
                    コメント：{template.comment}
                  </p>
                )}
              </div>
            </div>
          ),
        };
      });
    },
    [monthlyCheck?.templates, currentCheckboxs]
  );

  const hasAdminComment = !!monthlyCheck?.admin_comment;
  const isAnswered = monthlyCheck?.has_answered === true;

  const navigate = useNavigate();

  const { control, handleSubmit, reset } = useForm({
    defaultValues: {
      comfirmation_date: "",
      theme: "",
      content: "",
      inspection_results: [],
      comments: "",
    },
    resolver: zodResolver(MonthlyInspectionDetailSchema),
  });

  const handleCurrentCheckboxs = useCallback(
    (checkedValues: boolean[]) => {
      if (!monthlyCheck?.templates) return;

      const checkedTemplateIds = checkedValues
        .map((isChecked, index) => {
          if (isChecked && monthlyCheck.templates[index]) {
            return monthlyCheck.templates[index].id;
          }
          return null;
        })
        .filter((id): id is number => id !== null);

      setCurrentCheckboxs(checkedTemplateIds);
    },
    [monthlyCheck?.templates]
  );

  const handleClose = () => {
    const paramsString = createQueryString({
      [KEYS_SEARCH_PARAMS.PAGE]: currentPage,
      [KEYS_SEARCH_PARAMS.SEARCH]: currentSearch,
    });

    navigate(`/monthly-inspections?${paramsString}`);
    closeModal(MONTHLY_INSPECTION_DETAIL_MODAL_NAME);

    reset({
      comfirmation_date: monthlyCheck?.answer_date || new Date().toISOString(),
      comments: monthlyCheck?.admin_comment || "",
      content: monthlyCheck?.inspection.description || "",
      inspection_results: monthlyCheck?.current_answers || [],
      theme: monthlyCheck?.inspection.theme || "",
    });

    setCurrentCheckboxs([]);
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    handleSubmit((data) => {
      setIsSubmitting(true);
      answerMonthlyCheck({
        result_id: Number(id),
        answers: data.inspection_results,
      })
        .then((res) => {
          if (res.statusCode !== 200) return;
          fetchMonthlyChecks(
            {
              page: currentPage,
              theme: currentSearch,
            },
            0
          );
          handleClose();
        })
        .finally(() => setIsSubmitting(false));
    })();
  };

  useEffect(() => {
    if (!id) return;
    fetchMonthlyCheckById(Number(id));
  }, [currentPage, currentSearch, id, monthlyChecks.data.length]);

  useEffect(() => {
    if (!monthlyCheck) return;
    reset({
      comfirmation_date: monthlyCheck.answer_date || new Date().toISOString(),
      comments: monthlyCheck.admin_comment || "",
      content: monthlyCheck.inspection.description,
      inspection_results: monthlyCheck?.current_answers || [],
      theme: monthlyCheck.inspection.theme,
    });

    handleCurrentCheckboxs(monthlyCheck?.current_answers || []);
  }, [monthlyCheck]);

  return {
    handleClose,
    open,
    control,
    onSubmit,
    inspectionResultOptions,
    hasAdminComment,
    isSubmitting,
    isAnswered,
    handleCurrentCheckboxs,
    monthlyCheck,
  };
};

export { useMonthlyInspectionDetail };
